import { useState } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Check, Loader2, AlertTriangle, MessageSquare, ArrowRight, ChevronLeft, BookOpen } from 'lucide-react';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

const STORAGE_KEY = 'twoDownAutoPlus_gameState';
const ROUND_ID_KEY = 'twoDownAutoPlus_roundId';

export interface SettlementLine {
  fromName: string;
  toName: string;
  amount: number;
  venmoLink?: string;
  venmoWebLink?: string;
  paypalLink?: string;
  settlementId?: string;
  settlementStatus?: string;
  winnerPaymentMethods?: {
    venmo: string | null;
    paypal: string | null;
    zelle: string | null;
  };
}

interface SettlementStepperProps {
  lines: SettlementLine[];
  roundId?: string;
  courseName?: string;
  matchFormat?: '1v1' | '2v2';
  winningTeamNames?: string[];
  losingTeamNames?: string[];
  onClose: () => void;
}

type PayoutTier = { label: string; descriptor: string; discount: number; rate: number };

const PAYOUT_OPTIONS: PayoutTier[] = [
  { label: 'Full Pay', descriptor: 'Full amount owed', discount: 0, rate: 1 },
  { label: 'Quick Pay', descriptor: "70% of amount (you're being cool)", discount: 30, rate: 0.7 },
  { label: 'Holiday Pay', descriptor: '50% of amount (holiday spirit)', discount: 50, rate: 0.5 },
];

function computePayout(amount: number, rate: number) {
  return Math.round(amount * rate);
}

function fmt$(n: number) {
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

function buildAcceptsLabel(wp?: SettlementLine['winnerPaymentMethods']): string {
  const parts: string[] = [];
  if (wp?.venmo) parts.push(`Venmo (@${wp.venmo})`);
  if (wp?.paypal) parts.push(`PayPal (${wp.paypal})`);
  if (wp?.zelle) parts.push(`Zelle (${wp.zelle})`);
  parts.push('Cash');
  return parts.join(', ');
}

function buildAcceptsText(toName: string, wp?: SettlementLine['winnerPaymentMethods']): string {
  const parts: string[] = [];
  if (wp?.venmo) parts.push(`Venmo (@${wp.venmo})`);
  if (wp?.paypal) parts.push(`PayPal (${wp.paypal})`);
  if (wp?.zelle) parts.push(`Zelle (${wp.zelle})`);
  if (parts.length === 0) return `Settle up directly with ${toName}.`;
  parts.push('Cash');
  return `${toName} accepts ${parts.join(' or ')}.`;
}

export function SettlementStepper({ lines, roundId, courseName, matchFormat, winningTeamNames, losingTeamNames, onClose }: SettlementStepperProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const isTied = lines.length === 0 || lines[0]?.amount === 0;
  const [step, setStep] = useState(isTied ? 4 : 1);
  const [payoutTier, setPayoutTier] = useState<PayoutTier | null>(null);
  const [textsSent, setTextsSent] = useState<Record<number, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAdjustedAmount = (amount: number) => {
    if (!payoutTier) return amount;
    return computePayout(amount, payoutTier.rate);
  };

  const buildSmsText = (line: SettlementLine) => {
    const amount = getAdjustedAmount(line.amount);
    const paymentInfo = buildAcceptsText(line.toName, line.winnerPaymentMethods);
    return `Hey ${line.fromName}, you owe ${line.toName} ${fmt$(amount)} for today's match${courseName ? ` at ${courseName}` : ''}. ${paymentInfo}`;
  };

  const handleSendText = (idx: number) => {
    const text = buildSmsText(lines[idx]);
    const smsUrl = `sms:?body=${encodeURIComponent(text)}`;
    window.open(smsUrl, '_self');
    setTextsSent(prev => ({ ...prev, [idx]: true }));
  };

  const handleCompleteRound = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line.settlementId) continue;
        await apiRequest('POST', `/api/settlements/${line.settlementId}/confirm`, { paymentMethod: 'other' });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/pending'] });
      if (roundId) {
        queryClient.invalidateQueries({ queryKey: ['/api/rounds', roundId, 'lifecycle'] });
      }
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ROUND_ID_KEY);
      toast({
        title: isTied
          ? "Your even match has been recorded and pushed to the Ledger."
          : "Round saved and pushed to the Ledger.",
        duration: 4000,
      });
      onClose();
      await new Promise(resolve => setTimeout(resolve, 700));
      navigate('/ledger');
    } catch (err: any) {
      setSubmitting(false);
      setError(err?.message || 'Failed to complete settlement. Please try again.');
    }
  };

  const stepIndicator = isTied ? null : (
    <div className="flex items-center justify-center gap-1 mb-4">
      {[1, 2, 3, 4].map(s => (
        <div
          key={s}
          className={`h-1.5 rounded-full transition-all ${s === step ? 'w-6 bg-[#064E3B]' : s < step ? 'w-4 bg-[#064E3B]/40' : 'w-4 bg-gray-200'}`}
        />
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={submitting ? undefined : onClose} data-testid="settlement-stepper-overlay">
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white rounded-xl shadow-xl mx-4 max-w-sm w-full max-h-[85dvh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white rounded-t-xl px-5 pt-4 pb-2 z-10">
          {!submitting && (
            <button type="button" onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none" data-testid="stepper-close">&times;</button>
          )}
          {stepIndicator}
        </div>

        <div className="px-5 pb-5">
          {step === 1 && (
            <div data-testid="stepper-step-1">
              <p className="font-bold text-base text-gray-800 mb-1">Step 1 · Payout Amount</p>
              <p className="text-xs text-gray-500 mb-3">Choose the payout level for this round.</p>
              {lines.length > 1 && !lines.every(l => l.amount === lines[0]?.amount) && (
                <p className="text-xs text-amber-600 font-semibold mb-2" data-testid="stepper-amount-mismatch-warning">Settlement amounts differ — contact organizer</p>
              )}
              <div className="space-y-2">
                {PAYOUT_OPTIONS.map(opt => {
                  const perPlayerOriginal = lines[0]?.amount ?? 0;
                  const perPlayerAdjusted = computePayout(perPlayerOriginal, opt.rate);
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      className={`w-full text-left rounded-lg px-4 py-3 border transition-colors ${payoutTier?.label === opt.label ? 'bg-[#064E3B]/5 border-[#064E3B] ring-1 ring-[#064E3B]' : 'bg-gray-50 hover:bg-gray-100 border-gray-200'}`}
                      onClick={() => setPayoutTier(opt)}
                      data-testid={`payout-tier-${opt.discount}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold text-sm">{opt.label}</span>
                          <span className="block text-xs font-bold text-gray-500 mt-0.5">{opt.descriptor}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-gray-800">{fmt$(perPlayerAdjusted)}</span>
                          {opt.discount > 0 && (
                            <span className="block text-xs font-bold text-green-600">save {fmt$(perPlayerOriginal - perPlayerAdjusted)}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <Button
                className="w-full mt-4 bg-[#064E3B] hover:bg-[#065F46] text-white"
                disabled={!payoutTier}
                onClick={() => setStep(2)}
                data-testid="stepper-next-1"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="stepper-step-2">
              <p className="font-bold text-base text-gray-800 mb-1">Step 2 · Who Pays Who</p>
              <p className="text-xs text-gray-500 mb-3">Review the settlement breakdown.</p>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-testid="routing-summary">
                <div className="text-center">
                  <p className="text-sm font-semibold text-green-700 mb-0.5">
                    {winningTeamNames && winningTeamNames.length > 0
                      ? winningTeamNames.join(' & ')
                      : lines.map(l => l.toName).filter((v, i, a) => a.indexOf(v) === i).join(' & ')}
                  </p>
                  <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-0.5">beat</p>
                  <p className="text-sm font-semibold text-red-600 mb-2">
                    {losingTeamNames && losingTeamNames.length > 0
                      ? losingTeamNames.join(' & ')
                      : lines.map(l => l.fromName).filter((v, i, a) => a.indexOf(v) === i).join(' & ')}
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    {fmt$(getAdjustedAmount(lines[0]?.amount ?? 0))}
                    {matchFormat === '2v2' ? ' per player' : ''}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="stepper-back-2">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button className="flex-1 bg-[#064E3B] hover:bg-[#065F46] text-white" onClick={() => setStep(3)} data-testid="stepper-next-2">
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="stepper-step-3">
              <p className="font-bold text-base text-gray-800 mb-1">Step 3 · How to Pay</p>
              <p className="text-sm font-bold text-gray-500 mb-3">Send payment details to each player.</p>
              <div className="space-y-3">
                {lines.map((line, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-testid={`pay-line-${idx}`}>
                    <div className="mb-2">
                      <p className="text-base font-bold text-gray-800">
                        <span className="text-red-600">{line.fromName}</span>
                        {' owes '}
                        <span className="text-green-600">{line.toName}</span>
                        {' — '}
                        <span>{fmt$(getAdjustedAmount(line.amount))}</span>
                      </p>
                      <p className="text-sm font-bold text-gray-500 mt-1">
                        {line.toName} accepts: {buildAcceptsLabel(line.winnerPaymentMethods)}
                      </p>
                    </div>
                    <div className="bg-white rounded-md p-2.5 border border-gray-100 text-sm font-semibold text-gray-600 mb-2 leading-relaxed">
                      {buildSmsText(line)}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleSendText(idx)}
                        className="flex-1 flex items-center justify-center gap-2 bg-[#064E3B] hover:bg-[#065F46] text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
                        data-testid={`send-text-${idx}`}
                      >
                        <MessageSquare className="w-4 h-4" />
                        {textsSent[idx] ? 'Send Again' : `Text ${line.fromName}`}
                      </button>
                      {textsSent[idx] && (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium whitespace-nowrap">
                          <Check className="w-3 h-3" /> Sent
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1" data-testid="stepper-back-3">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button className="flex-1 bg-[#064E3B] hover:bg-[#065F46] text-white" onClick={() => setStep(4)} data-testid="stepper-next-3">
                  {Object.keys(textsSent).length > 0 ? 'Continue' : 'Skip & Continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="stepper-step-4">
              {isTied ? (
                <>
                  <div className="text-center py-4 mb-4">
                    <p className="font-bold text-lg text-gray-800">Match Tied</p>
                    <p className="text-sm text-gray-500 mt-1">No payouts needed — close out the round.</p>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-bold text-base text-gray-800 mb-1">Step 4 · Complete Round</p>
                  <p className="text-xs text-gray-500 mb-3">Finalize the settlement and close out the round.</p>
                  <div className="space-y-2 mb-4">
                    {lines.map((line, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-200" data-testid={`summary-line-${idx}`}>
                        <p className="text-sm font-bold">
                          <span className="text-red-600">{line.fromName}</span>
                          <span className="text-gray-400"> → </span>
                          <span className="text-green-600">{line.toName}</span>
                        </p>
                        <p className="font-bold text-sm">{fmt$(getAdjustedAmount(line.amount))}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 mb-3" data-testid="stepper-error">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <Button
                className="w-full bg-[#064E3B] hover:bg-[#065F46] text-white font-semibold"
                disabled={submitting}
                onClick={handleCompleteRound}
                data-testid="stepper-complete-round"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><BookOpen className="w-4 h-4 mr-2" /> Push to Ledger</>
                )}
              </Button>
              {!submitting && !isTied && (
                <Button variant="outline" onClick={() => setStep(3)} className="w-full mt-2" data-testid="stepper-back-4">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
